'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceDetail {
  icon: string;
  title: string;
  description: string;
  features: string[];
  benefits: string[];
  process: string[];
  image: string;
  alt: string;
}

interface ServiceDetailModalProps {
  service: ServiceDetail | null;
  onClose: () => void;
}

const ServiceDetailModal = ({ service, onClose }: ServiceDetailModalProps) => {
  if (!service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-popover rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-popover border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-2xl font-display font-bold text-foreground">{service.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors duration-300 ease-brand"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="relative w-full h-64 rounded-lg overflow-hidden">
            <AppImage
              src={service.image}
              alt={service.alt}
              className="w-full h-full object-cover"
            />
          </div>

          <div>
            <p className="text-muted-foreground leading-relaxed">{service.description}</p>
          </div>

          <div>
            <h3 className="text-lg font-display font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="SparklesIcon" size={20} className="text-accent" />
              <span>Key Features</span>
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm text-foreground">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-display font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="TrophyIcon" size={20} className="text-accent" />
              <span>Benefits</span>
            </h3>
            <ul className="space-y-2">
              {service.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm text-foreground">
                  <Icon name="ArrowTrendingUpIcon" size={16} className="text-conversion flex-shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-display font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="ClipboardDocumentListIcon" size={20} className="text-accent" />
              <span>Implementation Process</span>
            </h3>
            <ol className="space-y-3">
              {service.process.map((step, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <p className="text-sm text-foreground pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Pricing</p>
                <p className="text-lg font-display font-bold text-foreground">Customized quote based on your needs</p>
              </div>
              <a href="/contact" className="px-6 py-3 bg-accent text-accent-foreground rounded-md font-semibold hover:bg-accent/90 transition-all duration-300 ease-brand shadow-subtle">
                Request Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailModal;